import { useCallback, useEffect, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { KioskPortal } from './components/KioskPortal'
import { LoginScreen, PasswordRecoveryScreen } from './components/LoginScreen'
import { MfaChallengeScreen } from './components/MfaChallengeScreen'
import { ManagerDashboard } from './components/ManagerDashboard'
import { BrandMark } from './components/BrandMark'
import { supabase } from './lib/supabase'
import type { Workspace, WorkspaceVenue } from './types'

const requestedMode = new URLSearchParams(window.location.search).get('mode')

function ManagerPortal() {
  const [session, setSession] = useState<Session | null>(null)
  const [authReady, setAuthReady] = useState(false)
  const [mfaRequired, setMfaRequired] = useState(false)
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [venue, setVenue] = useState<WorkspaceVenue | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadWorkspace = useCallback(async () => {
    const { data, error: rpcError } = await supabase.rpc('get_compliance_workspace')
    if (rpcError) {
      setError(rpcError.message)
      setWorkspace(null)
      setVenue(null)
      return null
    }

    const nextWorkspace = data as Workspace
    const storedVenueId = localStorage.getItem('ventuhi_compliance_manager_venue')
    const nextVenue =
      nextWorkspace.venues.find((item) => item.id === storedVenueId) ??
      nextWorkspace.venues[0] ??
      null

    setWorkspace(nextWorkspace)
    setVenue(nextVenue)
    setError(null)

    return { workspace: nextWorkspace, venue: nextVenue }
  }, [])

  const venueNeedsMfa = useCallback(async (nextVenue: WorkspaceVenue | null) => {
    if (!nextVenue?.management_mfa_required) return false

    const { data: assurance, error: assuranceError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
    if (assuranceError) throw assuranceError

    return assurance?.nextLevel === 'aal2' && assurance.currentLevel !== 'aal2'
  }, [])

  const prepareAuthenticatedSession = useCallback(async (nextSession: Session | null) => {
    setSession(nextSession)

    if (!nextSession) {
      setMfaRequired(false)
      setWorkspace(null)
      setVenue(null)
      setError(null)
      return
    }

    const loaded = await loadWorkspace()
    if (!loaded) return

    try {
      setMfaRequired(await venueNeedsMfa(loaded.venue))
    } catch (assuranceError) {
      setError(assuranceError instanceof Error ? assuranceError.message : 'MFA status could not be checked.')
      setMfaRequired(false)
    }
  }, [loadWorkspace, venueNeedsMfa])

  useEffect(() => {
    let active = true

    void supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return
      await prepareAuthenticatedSession(data.session)
      if (active) setAuthReady(true)
    })

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!active) return
      void prepareAuthenticatedSession(nextSession).finally(() => {
        if (active) setAuthReady(true)
      })
    })

    return () => {
      active = false
      listener.subscription.unsubscribe()
    }
  }, [prepareAuthenticatedSession])

  async function handleMfaVerified() {
    const { data, error: sessionError } = await supabase.auth.getSession()
    if (sessionError) {
      setError(sessionError.message)
      return
    }
    await prepareAuthenticatedSession(data.session)
  }

  function selectVenue(nextVenue: WorkspaceVenue) {
    localStorage.setItem('ventuhi_compliance_manager_venue', nextVenue.id)
    setVenue(nextVenue)

    void venueNeedsMfa(nextVenue)
      .then(setMfaRequired)
      .catch((assuranceError) => {
        setError(assuranceError instanceof Error ? assuranceError.message : 'MFA status could not be checked.')
      })
  }

  async function refreshWorkspace() {
    const loaded = await loadWorkspace()
    if (!loaded) return

    try {
      setMfaRequired(await venueNeedsMfa(loaded.venue))
    } catch (assuranceError) {
      setError(assuranceError instanceof Error ? assuranceError.message : 'MFA status could not be checked.')
    }
  }

  if (!authReady) {
    return <main className="loading-screen"><BrandMark /><div className="spinner" /><p>Opening VenTuhi Compliance…</p></main>
  }

  if (!session) return <LoginScreen />
  if (mfaRequired) return <MfaChallengeScreen onVerified={handleMfaVerified} />

  if (error) {
    return (
      <main className="loading-screen">
        <BrandMark />
        <div className="message message--error">{error}</div>
        <p>This account does not currently have access to the Compliance portal.</p>
        <button className="button button--ghost" onClick={() => void supabase.auth.signOut()}>Sign out</button>
      </main>
    )
  }

  if (!workspace || !venue) {
    return <main className="loading-screen"><BrandMark /><div className="spinner" /><p>Loading venue workspace…</p></main>
  }

  return <ManagerDashboard workspace={workspace} venue={venue} onVenueChange={selectVenue} onWorkspaceRefresh={refreshWorkspace} />
}

export default function App() {
  if (requestedMode === 'kiosk') return <KioskPortal />
  if (requestedMode === 'recovery') return <PasswordRecoveryScreen />
  return <ManagerPortal />
}
